import { DateInput } from '../../../../components/DateInput/DateInput';
import React, { useState, useEffect } from 'react';
import { profileService, UserResidentialStatusDto } from '../../../../services/profile.service';
import { toast } from 'react-toastify';
import { validateResidentialStatus, localTodayIso } from '../../../../utils/investorValidation';
import { SharedFormContext } from '../shared/types';
import { useLiveErrorClearing } from '../shared/useLiveErrorClearing';
import { useDelegationPermissions } from '../../../../contexts/DelegationPermissionsContext';
import { agentReadOnlyNote } from '../../../../utils/delegationScope';
import { getPermissionErrorMessage } from '../../../../utils/apiClient';
import { PremiumSelect } from '../../../../components/PremiumSelect/PremiumSelect';
import { isResidentIndividual, isOci, isNri } from '../../../../config/profileVisibility';

/** H-15 lock tooltip — same wording the Personal Info and Passport forms use. */
const OCR_LOCK_TITLE = 'Verified from your document — change it in the Documents Center';

// Parity fix: values must match Laravel's stored/validated tokens exactly ("Visa" / "Resident Proof").
// They were UPPERCASE, which left the conditional visa/resident blocks + their validation dead.
const TYPE_OF_PROOF_OPTIONS = [
  { value: 'Visa', label: 'Visa' },
  { value: 'Resident Proof', label: 'Resident Card' },
];

/** RI-only validation: Aadhaar Number is required and must be 12 digits. */
function validateRiAadhaar(formData: Partial<UserResidentialStatusDto>): Record<string, string> {
  const errors: Record<string, string> = {};
  const aadhaar = (formData.aadharNumber ?? '').replace(/\s+/g, '');
  if (!aadhaar) {
    errors.aadharNumber = 'Aadhaar Number is required.';
  } else if (!/^\d{12}$/.test(aadhaar)) {
    errors.aadharNumber = 'Aadhaar Number must be 12 digits.';
  }
  return errors;
}

interface ResidentialStatusFormProps {
  initialData: UserResidentialStatusDto | null;
  onSave: () => void;
  canEdit: boolean;
  canSubmit: boolean;
  isProxyMode: boolean;
  sharedContext: SharedFormContext;
}

export const ResidentialStatusForm: React.FC<ResidentialStatusFormProps> = ({
  initialData,
  onSave,
  canEdit,
  isProxyMode,
  sharedContext
}) => {
  const delegationPerms = useDelegationPermissions();
  const [formData, setFormData] = useState<Partial<UserResidentialStatusDto>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  // RI renames this tab to "Aadhaar Details" and hides everything except
  // Aadhaar Number and Name on Aadhaar. Residential-status value is stamped
  // to "Resident Indian" server-side on save.
  const investorType = sharedContext.dashboardData?.investor?.investorType ?? undefined;
  const isRi = isResidentIndividual(investorType);
  const isOciInvestor = isOci(investorType);
  const isNriInvestor = isNri(investorType);

  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
    }
  }, [initialData]);

  // Corrected fields drop their error immediately (remove-only live re-check).
  useLiveErrorClearing(errors, setErrors, () => (isRi ? validateRiAadhaar(formData) : validateResidentialStatus(formData as UserResidentialStatusDto, { isOci: isOciInvestor, isNri: isNriInvestor })), [formData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // RI bypasses the full residential-status validation because most of the
    // fields it checks (Residential Status dropdown, proof type, visa details)
    // are hidden and default-stamped server-side.
    const newErrors = isRi
      ? validateRiAadhaar(formData)
      : validateResidentialStatus(formData as UserResidentialStatusDto, {
          isOci: isOciInvestor,
          isNri: isNriInvestor,
        });
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      const firstError = Object.values(newErrors)[0];
      toast.error(firstError ? `Please fix: ${firstError}` : 'Please fix the errors in the form');
      return;
    }

    setErrors({});
    setSaving(true);

    // Laravel parity (investor_residential_status_submit): clear the OTHER proof type's
    // fields so stale data never lingers in the row or the generated PDF. NRI-only —
    // exactly where the proof block renders; OCI/RI payloads are untouched. Nulls flow
    // through the server's unconditional writes, same net effect as Laravel's server wipe.
    const payload: UserResidentialStatusDto = { ...(formData as UserResidentialStatusDto) };
    if (isNriInvestor && payload.userTypeOfProof === 'Resident Proof') {
      payload.userVisaType = null as any;
      payload.userVisaNumber = null as any;
      payload.userVisaIssuerDate = null as any;
      payload.userVisaExpiryDate = null as any;
    } else if (isNriInvestor && payload.userTypeOfProof === 'Visa') {
      payload.userVisaDateOfIssue = null as any;
      payload.userVisaValidUpto = null as any;
    }

    try {
      await profileService.updateResidentialStatus(payload);
      toast.success('Residential status updated');
      onSave();
    } catch (err: any) {
      const permissionError = getPermissionErrorMessage(err);
      if (permissionError) {
        toast.error(permissionError);
      } else {
        toast.error(err.response?.data?.message || 'Failed to update');
      }
    } finally {
      setSaving(false);
    }
  };

  if (isRi) {
    // RI Aadhaar Details — minimal form. Residential status, proof of
    // address, OCI fields and visa block are all hidden; the backend stamps
    // residentialStatus = "Resident Indian" on save.
    //
    // H-15: both inputs are the RI journey's ONLY view of the Aadhaar the
    // investor accepted from OCR, so they lock exactly like the Personal Info
    // and Passport fields do. Target-field keys are the ones the import audits
    // in KycProfileImportService.applyToResidentialStatus.
    const aadhaarLocked = sharedContext.isOcrLocked(
      'investor_residential_status.aadhar_number', formData.aadharNumber);
    const nameOnAadhaarLocked = sharedContext.isOcrLocked(
      'investor_residential_status.name_on_aadhaar', formData.nameOnAadhaar);
    return (
      <form className="investor-profile__card" onSubmit={handleSubmit}>
        <div className="investor-profile__grid">
          <div className="form-group">
            <label>Aadhaar Number<span className="text-danger">*</span></label>
            <input
              type="text"
              maxLength={12}
              className={errors.aadharNumber ? 'form-control is-invalid' : 'form-control'}
              value={formData.aadharNumber ?? ''}
              onChange={(e) => setFormData({ ...formData, aadharNumber: e.target.value })}
              disabled={aadhaarLocked}
              title={aadhaarLocked ? OCR_LOCK_TITLE : undefined}
              placeholder="12-digit Aadhaar number"
            />
            {errors.aadharNumber && <div className="invalid-feedback">{errors.aadharNumber}</div>}
          </div>
          <div className="form-group">
            <label>Name on Aadhaar</label>
            <input
              type="text"
              className="form-control"
              value={formData.nameOnAadhaar ?? ''}
              onChange={(e) => setFormData({ ...formData, nameOnAadhaar: e.target.value })}
              disabled={nameOnAadhaarLocked}
              title={nameOnAadhaarLocked ? OCR_LOCK_TITLE : undefined}
              placeholder="Name as printed on your Aadhaar card"
            />
          </div>
        </div>
        {!sharedContext.lockedAfterSubmit && (
          <button type="submit" className="btn-save" disabled={saving || !canEdit}>
          {saving ? 'Submitting...' : 'Submit'}
          </button>
        )}
        {!canEdit && agentReadOnlyNote(delegationPerms, 'KYC') && (
          <small className="text-warning d-block mt-2">
            {agentReadOnlyNote(delegationPerms, 'KYC')}
          </small>
        )}
      </form>
    );
  }

  return (
    <form className="investor-profile__card" onSubmit={handleSubmit}>
      {/* <h3>Residential Status &amp; Proof of Address</h3> */}
      <div className="investor-profile__grid">

        {/* OCI card block — Laravel blade:1217 renders this only for OCI. */}
        {isOciInvestor && (
          <>
            <div className="form-group">
              <label>OCI Card No<span className="text-danger">*</span></label>
              <input
                type="text"
                className={errors.userOciCardNo ? 'form-control is-invalid' : 'form-control'}
                value={formData.userOciCardNo ?? ''}
                onChange={(e) => setFormData({ ...formData, userOciCardNo: e.target.value })}
              />
              {errors.userOciCardNo && <div className="invalid-feedback">{errors.userOciCardNo}</div>}
            </div>

            <div className="form-group">
              <label>Issue Date<span className="text-danger">*</span></label>
              <DateInput
                className={errors.userOciIssueDate ? 'form-control is-invalid' : 'form-control'}
                value={formData.userOciIssueDate ?? ''}
                onChange={(value) => setFormData({ ...formData, userOciIssueDate: value })}
                max={localTodayIso()}
              />
              {errors.userOciIssueDate && <div className="invalid-feedback d-block">{errors.userOciIssueDate}</div>}
            </div>

            <div className="form-group">
              <label>Valid Upto</label>
              <DateInput
                className={errors.userOciValidUpto ? 'form-control is-invalid' : 'form-control'}
                value={formData.userOciValidUpto ?? ''}
                onChange={(value) => setFormData({ ...formData, userOciValidUpto: value })}
                min={localTodayIso()}
              />
              {errors.userOciValidUpto && <div className="invalid-feedback d-block">{errors.userOciValidUpto}</div>}
            </div>
          </>
        )}

        {/* Type of Proof + its visa / resident-card blocks — Laravel blade:1264
            renders these only for NRI. */}
        {isNriInvestor && (
          <div className="form-group">
            <label>Type of Proof<span className="text-danger">*</span></label>
            <PremiumSelect
              value={formData.userTypeOfProof ?? ''}
              onChange={(val) => setFormData({ ...formData, userTypeOfProof: val })}
              options={TYPE_OF_PROOF_OPTIONS}
              placeholder="Select Type of Proof"
              error={errors.userTypeOfProof}
            />
          </div>
        )}

        {/* Visa Fields - Shown when Type of Proof = "Visa" */}
        {isNriInvestor && formData.userTypeOfProof === 'Visa' && (
          <>
            <div className="form-group">
              <label>Visa Types<span className="text-danger">*</span></label>
              <input
                type="text"
                className={errors.userVisaType ? 'form-control is-invalid' : 'form-control'}
                value={formData.userVisaType ?? ''}
                onChange={(e) => setFormData({ ...formData, userVisaType: e.target.value })}
              />
              {errors.userVisaType && <div className="invalid-feedback">{errors.userVisaType}</div>}
            </div>

            <div className="form-group">
              <label>Visa Number<span className="text-danger">*</span></label>
              <input
                type="text"
                className={errors.userVisaNumber ? 'form-control is-invalid' : 'form-control'}
                value={formData.userVisaNumber ?? ''}
                onChange={(e) => setFormData({ ...formData, userVisaNumber: e.target.value })}
              />
              {errors.userVisaNumber && <div className="invalid-feedback">{errors.userVisaNumber}</div>}
            </div>

            <div className="form-group">
              <label>Visa Issuer Date<span className="text-danger">*</span></label>
              <DateInput
                className={errors.userVisaIssuerDate ? 'form-control is-invalid' : 'form-control'}
                value={formData.userVisaIssuerDate ?? ''}
                onChange={(value) => setFormData({ ...formData, userVisaIssuerDate: value })}
                max={localTodayIso()}
              />
              {errors.userVisaIssuerDate && <div className="invalid-feedback d-block">{errors.userVisaIssuerDate}</div>}
            </div>

            <div className="form-group">
              <label>Visa Expiry Date<span className="text-danger">*</span></label>
              {/* Laravel parity: the expiry calendar's floor follows the issuer date. */}
              <DateInput
                className={errors.userVisaExpiryDate ? 'form-control is-invalid' : 'form-control'}
                value={formData.userVisaExpiryDate ?? ''}
                onChange={(value) => setFormData({ ...formData, userVisaExpiryDate: value })}
                min={formData.userVisaIssuerDate && formData.userVisaIssuerDate > localTodayIso()
                  ? formData.userVisaIssuerDate
                  : localTodayIso()}
              />
              {errors.userVisaExpiryDate && <div className="invalid-feedback d-block">{errors.userVisaExpiryDate}</div>}
            </div>
          </>
        )}

        {/* Resident Card Fields — Laravel's toggleProofDivs shows this block for
            anything that is NOT "Visa", including the unselected state. */}
        {isNriInvestor && formData.userTypeOfProof !== 'Visa' && (
          <>
            <div className="form-group">
              <label>Date Of Issue<span className="text-danger">*</span></label>
              <DateInput
                className={errors.userVisaDateOfIssue ? 'form-control is-invalid' : 'form-control'}
                value={formData.userVisaDateOfIssue ?? ''}
                onChange={(value) => setFormData({ ...formData, userVisaDateOfIssue: value })}
                max={localTodayIso()}
              />
              {errors.userVisaDateOfIssue && <div className="invalid-feedback d-block">{errors.userVisaDateOfIssue}</div>}
            </div>

            <div className="form-group">
              <label>Valid Upto</label>
              <DateInput
                className={errors.userVisaValidUpto ? 'form-control is-invalid' : 'form-control'}
                value={formData.userVisaValidUpto ?? ''}
                onChange={(value) => setFormData({ ...formData, userVisaValidUpto: value })}
                min={localTodayIso()}
              />
              {errors.userVisaValidUpto && <div className="invalid-feedback d-block">{errors.userVisaValidUpto}</div>}
            </div>
          </>
        )}
      </div>

      {!sharedContext.lockedAfterSubmit && (
        <button type="submit" className="btn-save" disabled={saving || !canEdit}>
        {saving ? 'Submitting...' : 'Submit'}
        </button>
      )}
      {!canEdit && agentReadOnlyNote(delegationPerms, 'KYC') && (
        <small className="text-warning d-block mt-2">
          {agentReadOnlyNote(delegationPerms, 'KYC')}
        </small>
      )}
    </form>
  );
};
